/**
 * Spec 022: the price on a guest car card. A brand shows the daily price, the week price or,
 * since spec 028, the monthly price (`myBrandRules.carCardPrice`, D8); the home's price tabs
 * switch it (D13), and monthly places always show the monthly price (D9). The week price is
 * `tripSettings.displayPrices.oneWeek`, computed by the server with the discount this brand
 * applies (D12), stored with two decimals and shown in whole dollars (D7). Tested in
 * `carCardPrice.test.ts`.
 */

export type CarCardPriceMode = 'daily' | 'weekly' | 'monthly';
/** What a card shows: a home mode, or the monthly price of a monthly section. */
export type CarCardPriceSlot = CarCardPriceMode;
const PRICE_MODES: readonly CarCardPriceMode[] = ['daily', 'weekly', 'monthly'];

export interface CarDisplayPricesLike {
  threeDays?: number | null;
  oneWeek?: number | null;
  twoWeeks?: number | null;
  threeWeeks?: number | null;
}

export interface CarCardPriceInput {
  slot: CarCardPriceSlot;
  displayPrices?: CarDisplayPricesLike | null;
  dailyPrice?: number | null;
  monthlyPrice?: number | null;
}

export interface CarCardPriceView {
  amount: number | null | undefined;
  suffix: '/day' | '/wk' | '/mo';
}

/** The brand switch as the API sends it; anything but `weekly` or `monthly` is today's daily card. */
export function carCardPriceModeOf(raw: string | null | undefined): CarCardPriceMode {
  return raw === 'weekly' || raw === 'monthly' ? raw : 'daily';
}

/** The home's price tabs: the brand's mode first, then the others in the order daily, weekly, monthly (D10, D13, spec 028). */
export function homePriceTabs(brandMode: CarCardPriceMode): CarCardPriceMode[] {
  return [brandMode, ...PRICE_MODES.filter(mode => mode !== brandMode)];
}

export const CAR_CARD_PRICE_TAB_LABELS: Record<CarCardPriceMode, string> = {
  daily: 'Daily',
  weekly: 'Weekly',
  monthly: 'Monthly',
};

function usable(value: number | null | undefined): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0;
}

/**
 * Amount and suffix for one card. Weekly without a week price (no weekday price, an older API)
 * and monthly without a monthly price fall back to the daily price. Daily amounts are shown as
 * the API sends them.
 */
export function carCardPrice({
  slot,
  displayPrices,
  dailyPrice,
  monthlyPrice,
}: CarCardPriceInput): CarCardPriceView {
  if (slot === 'monthly' && usable(monthlyPrice)) return { amount: monthlyPrice, suffix: '/mo' };
  const week = displayPrices?.oneWeek;
  if (slot === 'weekly' && usable(week)) return { amount: Math.round(week), suffix: '/wk' };
  return { amount: dailyPrice, suffix: '/day' };
}

type WithTripLength = {
  tripSettings?: { maximumRentalDays?: number | null } | null;
  maximumRentalDays?: number | null;
  monthlyRentalIsOn?: boolean | null;
  monthlyPrice?: number | null;
};

/**
 * D13, D14: a car capped under 7 days is left out under Weekly. Null or 0 means no cap
 * (the server reads 0 as no maximum too). Takes any row: a host row has no `tripSettings`.
 */
export function offeredForAWeek(car: object | null | undefined): boolean {
  const max = (car as WithTripLength | null | undefined)?.tripSettings?.maximumRentalDays;
  return !(typeof max === 'number' && max > 0 && max < 7);
}

/**
 * Spec 028: a car a guest can rent by the month, the rule of `getMonthlyRentalCarsNearMe`: the
 * host's monthly switch on, a monthly price, and no cap under 30 days (`tripSettings` first,
 * else the car's own column; null or 0 means no cap).
 */
export function offeredForAMonth(car: object | null | undefined): boolean {
  const row = car as WithTripLength | null | undefined;
  if (!row || row.monthlyRentalIsOn !== true || !usable(row.monthlyPrice)) return false;
  const max = row.tripSettings?.maximumRentalDays ?? row.maximumRentalDays;
  return !(typeof max === 'number' && max > 0 && max < 30);
}

/**
 * Cars shown for a price mode: every car, except the ones capped under a week on Weekly; on
 * Monthly only the cars offered by the month.
 */
export function carsForPriceMode<T extends object>(
  cars: readonly T[],
  mode: CarCardPriceSlot,
): T[] {
  if (mode === 'weekly') return cars.filter(car => offeredForAWeek(car));
  if (mode === 'monthly') return cars.filter(car => offeredForAMonth(car));
  return [...cars];
}
